export interface SnappedAngleLine {
  end: [number, number];
  /** Undirected edge angle from horizontal, always in the 0..90 degree range. */
  degrees: number;
}

/**
 * Snap an NDC line to an angle measured in real screen pixels.
 *
 * NDC is stretched to fill the canvas, so using atan2 directly on NDC would
 * make a supposed 45 degree edge lean on a non-square viewport. Converting the
 * delta to pixels before snapping keeps the guide geometrically honest.
 */
export function snapNdcLine(
  start: [number, number],
  rawEnd: [number, number],
  viewportWidth: number,
  viewportHeight: number,
  stepDegrees: number,
): SnappedAngleLine {
  if (viewportWidth <= 0 || viewportHeight <= 0 || stepDegrees <= 0) {
    return { end: rawEnd, degrees: 0 };
  }

  const dxPixels = ((rawEnd[0] - start[0]) * viewportWidth) / 2;
  const dyPixels = ((rawEnd[1] - start[1]) * viewportHeight) / 2;
  const lengthPixels = Math.hypot(dxPixels, dyPixels);
  if (lengthPixels < 1e-6) return { end: start, degrees: 0 };

  const rawDegrees = (Math.atan2(dyPixels, dxPixels) * 180) / Math.PI;
  const snappedDegrees = Math.round(rawDegrees / stepDegrees) * stepDegrees;
  const radians = (snappedDegrees * Math.PI) / 180;
  const axisAngle = ((snappedDegrees % 180) + 180) % 180;

  return {
    end: [
      start[0] + (Math.cos(radians) * lengthPixels * 2) / viewportWidth,
      start[1] + (Math.sin(radians) * lengthPixels * 2) / viewportHeight,
    ],
    degrees: axisAngle > 90 ? 180 - axisAngle : axisAngle,
  };
}
